import TypedFroalaEditor from "froala-editor-typings";
import * as Sentry from "@sentry/react";
import Command from ".";
import { TenantUser } from "../../../types/auth";
import { checkDeletionSpan, isLineNumber, retrieveEditorContext } from "../Utility/inspectDOM";
import { handleAdditionNodeCreation, newAdditionNode } from "../Utility/modifyDOM";
import { handleSelectionDeletion } from "../Utility/selectionDeletion";
import Memento from "../Memento";
import { saveEditorSelectionAndAddEditorStateToMemento } from "../Utility/base";

class PasteCommand implements Command {
    private editor: TypedFroalaEditor.FroalaEditor;
    private event: ClipboardEvent;
    private user: TenantUser;
    private previousState: Memento;
    private spanId: string | null;

    constructor(editor: TypedFroalaEditor.FroalaEditor, event: ClipboardEvent, user: TenantUser) {
        this.editor = editor;
        this.event = event;
        this.user = user;
        this.previousState = this.saveState();
        this.spanId = null;
    }

    execute(): void {
        this.event.preventDefault();
        this.event.stopPropagation();
        this.insertFromClipboard();
    }

    undo(): void {
        this.restoreState(this.previousState);
    }

    saveState(): Memento {
        return saveEditorSelectionAndAddEditorStateToMemento(this.editor);
    }

    restoreState(memento: Memento): void {
        this.editor.html.set(memento.getState()); // Restore the HTML content
    }

    get previous(): Memento {
        return this.previousState;
    }

    get id(): string | null {
        return this.spanId;
    }

    private insertFromClipboard(): void {
        if (!this.event.clipboardData) {
            return;
        }

        const charTyped = this.event.clipboardData.getData("text");
        const selection: Selection = this.editor.selection.get();

        if (selection.isCollapsed) {
            const anchor = selection.anchorNode;
            const anchorOffset = selection.anchorOffset;

            // Do nothing for safety
            if (!anchor) {
                return;
            }

            const { anchorNode, charIndexed } = retrieveEditorContext(anchor, anchorOffset);

            if (isLineNumber(anchorNode)) {
                return;
            }

            if (charIndexed) {
                const checkDeletion = checkDeletionSpan(anchorNode);
                if (checkDeletion) {
                    // If we are at the end of a deletion, allow typing
                    if (anchorOffset === checkDeletion.textContent?.length) {
                        handleAdditionNodeCreation(
                            checkDeletion,
                            anchorOffset,
                            charTyped,
                            "right",
                            this.user!,
                        );
                    }

                    // Disallow typing inside deletion span
                    return;
                }

                // Handles addition inside a node
                handleAdditionNodeCreation(
                    anchorNode,
                    anchorOffset,
                    charTyped,
                    "inside",
                    this.user!,
                );
                return;
            } else {
                // In this case we are at a Node edge

                // Handles addition at an edge
                handleAdditionNodeCreation(
                    anchorNode,
                    anchorOffset,
                    charTyped,
                    "right",
                    this.user!,
                );

                return;
            }
        } else {
            // Delete selection
            const { lastNode } = handleSelectionDeletion(selection, this.user!);

            // Place span and cursor
            if (lastNode) {
                newAdditionNode(lastNode, charTyped, this.user!);
            } else {
                // TODO: what if no last node?
                Sentry.captureMessage("Selection + Addition failed to return lastNode", "warning");
            }
        }
    }
}

export default PasteCommand;
